import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import { EntityType, EventType, GqlService, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileManagerComponent, FileViewerComponent],
  selector: 'aux-import-patient-data-modal',
  templateUrl: 'import-patient-data-modal.component.html',
})
export class ImportPatientDataModalComponent {
  private apiService = inject(ApiService);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  errorMessage = signal('');

  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  loading$ = new BehaviorSubject(false);
  metadata: FileMetadata = {};
  ref = inject<
    CustomOverlayRef<
      unknown,
      {
        onSuccess: VoidFunction;
      }
    >
  >(CustomOverlayRef);

  constructor() {
    if (this.ref.data) {
      this.onSuccess = this.ref.data.onSuccess;
    }
  }

  async downloadPatientTrackerTemplate() {
    const { data, success } = await this.apiService.getTemplatePath(
      null,
      TemplateType.BULK_EDC_TEMPLATE
    );

    if (!(success && data)) {
      this.overlayService.error(MessagesConstants.FILE.PROBLEM_DOWNLOADING_TEMPLATE);
    } else {
      await this.apiService.downloadFileFromPath(data.id);
    }
  }

  getFilePath() {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/patient-tracker/`;
  }

  onSuccess: FunctionStringCallback = () => {};

  async onUpload() {
    this.errorMessage.set('');

    if (this.fileManager && !this.loading$.getValue()) {
      const files = this.fileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      this.loading$.next(true);

      const file = files[0];
      const key = `${this.getFilePath()}${file.bucket_key}`;

      this.fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: key,
      });

      const fileSuccess = await this.fileManager.fileService.uploadFiles({
        metadata: { admin: '1' },
      });

      if (fileSuccess) {
        const { data, errors, success } = await firstValueFrom(
          this.gqlService.validateEventFile$({
            bucket_key: `public/${key}`,
            entity_id: this.mainQuery.getValue().trialKey,
            entity_type: EntityType.TRIAL,
            type: EventType.BULK_EDC_TEMPLATE_UPLOADED,
          })
        );

        if (success && data?.id) {
          this.overlayService.success('Template validation started.');
          this.onSuccess(data.id);
        } else {
          await this.apiService.removeFile(key);
          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }

    this.loading$.next(false);
  }

  pathFn: () => string = () => '';
}
